import * as React from 'react';
import {RouteComponentProps, withRouter} from 'react-router';

import {
  ApplicationContextConsumer,
  bindLoadingHandler,
  LoadingOverlayHandler,
  withLoadingOverlay
} from '../app/ApplicationContext';
import {ErrorResponse, Invoice} from '../api/dtos/dtos';
import * as paths from '../paths';
import {ErrorAlert} from '../common/ErrorAlert';
import furoApi from "../api/FuroApi";
import ButtonBar from "../common/ButtonBar";
import {Button} from "antd";
import locales from "../locales";
import {InvoiceState, InvoiceType} from "./invoice";
import InvoiceEditor from "./InvoiceEditor";
import {setAppHeaderSubtitle} from "../app/AppHeader";
import {showConfirm, showDeleteConfirm, showErrorModal} from "../utils/dialogs";
import InvoiceHistoryTable from "./InvoiceHistoryTable";

interface State {
  editable: boolean;
  invoiceId: number;
  invoice?: Invoice;
  error?: ErrorResponse;
}

class InvoiceDetailsPage extends React.Component<RouteComponentProps<paths.IdPageUrlParams>, State> {
  public readonly loadingHandler?: LoadingOverlayHandler;
  public state: State = {
    editable: false,
    invoiceId: Number(this.props.match.params.id),
  };

  public componentDidMount() {
    this.loadInvoice();
  }

  public componentDidUpdate(prevProps: RouteComponentProps<paths.IdPageUrlParams>) {
    if (prevProps.match.params.id !== this.props.match.params.id) {
      this.setState({
        invoiceId: Number(this.props.match.params.id),
        invoice: undefined,
        error: undefined,
        editable: false,
      }, this.loadInvoice);
    }
  }

  public render(): React.ReactNode {
    const {error, editable, invoice} = this.state;

    return (
      <>
        <ApplicationContextConsumer>
          {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
        </ApplicationContextConsumer>
        {invoice?.state === InvoiceState.CREATED &&
            <ButtonBar style={{marginBottom: 24}}>
                <Button
                    type="default"
                    icon="send"
                    onClick={this.onSendClick}
                    disabled={editable}
                >{locales.invoice_send}</Button>
                <Button
                    type="danger"
                    icon="delete"
                    onClick={this.onDeleteClick}
                    disabled={editable}
                >{locales.delete}</Button>
                <Button
                    type="primary"
                    icon="edit"
                    onClick={this.onEditClick}
                    disabled={editable}
                >{locales.edit}</Button>
            </ButtonBar>
        }

        {invoice?.state === InvoiceState.SENT &&
            <ButtonBar style={{marginBottom: 24}}>
                <Button type="default" onClick={this.onViewClick} disabled={editable}>
                  {locales.invoice_open}
                </Button>
              {invoice?.type === InvoiceType.INVOICE &&
                  <Button type="danger" icon="stop" onClick={this.onStornoClick}>
                    {locales.storno}
                  </Button>
              }
            </ButtonBar>
        }

        {invoice?.state === InvoiceState.CANCELLED &&
            <ButtonBar style={{marginBottom: 24}}>
                <Button type="default" onClick={this.onViewClick} disabled={editable}>
                  {locales.invoice_open}
                </Button>
            </ButtonBar>
        }


        {invoice &&
            <InvoiceEditor
                key={invoice.id}
                invoice={invoice}
                client={invoice.client}
                source={invoice.source}
                entries={invoice.entries}
                editable={editable}
                onCancel={this.onCancel}
                onSubmit={this.onSubmit}
            />
        }


        {invoice &&
            <section style={{marginTop: 24}}>
                <h3>{locales.invoice_history}</h3>
                <InvoiceHistoryTable invoices={invoice.historyInvoices}/>
            </section>
        }

        <ErrorAlert style={{marginTop: 16}} error={error}/>
      </>
    );
  }

  private onCancel = () => {
    this.setState({editable: false});
  }

  private onViewClick = () => {
    window.open(this.state.invoice!.customerUrl, '_blank');
  };

  private onSubmit = () => {
    this.setState({editable: false});
    this.loadInvoice();
  };

  private onEditClick = () => {
    this.setState({editable: true});
  };

  private onSendClick = () => {
    showConfirm(locales.invoice_send_confirm, this.sendInvoice);
  };

  private onDeleteClick = () => {
    showDeleteConfirm(this.deleteInvoice, locales.invoice_delete_confirm);
  };

  private deleteInvoice = () => {
    const promise = furoApi.invoicing.deleteInvoice(this.state.invoiceId)
      .then(this.pop)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
    return promise;
  };

  private pop = () => {
    this.props.history.goBack();
  }

  private sendInvoice = () => {
    const promise = furoApi.invoicing.sendInvoice(this.state.invoiceId)
      .then(this.loadInvoice)
      .catch((error: ErrorResponse) => showErrorModal(error.details?.message || error.message));
    withLoadingOverlay(promise, this.loadingHandler);
  }

  private onStornoClick = () => {
    showConfirm(locales.storno_confirm, this.stornoInvoice);
  };

  private stornoInvoice = () => {
    const invoice = this.state.invoice!;
    const promise = furoApi.invoicing.stornoInvoice(invoice.id)
      .then(this.loadInvoice)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
    return promise;
  };

  private loadInvoice = () => {
    const invoiceId = this.state.invoiceId;
    const promise = furoApi.invoicing.getInvoice(invoiceId)
      .then((invoice) => {
        if (invoiceId !== Number(this.props.match.params.id)) {
          return;
        }
        this.setState({invoice, error: undefined})
        setAppHeaderSubtitle(this.props, invoice.externalNumber ?? invoice.internalNumber);
      })
      .catch((error: ErrorResponse) => {
        if (invoiceId === Number(this.props.match.params.id)) {
          this.onApiError(error);
        }
      });
    withLoadingOverlay(promise, this.loadingHandler);
  };


  private onApiError = (error: ErrorResponse) => this.setState({error});

}

export default withRouter(InvoiceDetailsPage);



// WEBPACK FOOTER //
// ./src/invoicing/InvoiceDetailsPage.tsx